import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function CaseStudiesTab() {
  const { user } = useAuth();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aiChecking, setAiChecking] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const aiQuota = useAiQuota();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const loadStudies = useCallback(() => {
    api.get('/dashboard/case-studies').then(r => setStudies(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStudies(); }, [loadStudies]);

  const lineCount = content.split('\n').filter(l => l.trim()).length;
  const isPremium = user?.tier === 'premium' || user?.tier === 'turbo';

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      if (editing) {
        await api.put(`/dashboard/case-studies/${editing.id}`, { title, content });
      } else {
        await api.post('/dashboard/case-studies', { title, content });
      }
      setShowForm(false); setEditing(null); setTitle(''); setContent(''); setAiResult(null);
      loadStudies();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    await api.delete(`/dashboard/case-studies/${id}`).catch(() => {});
    loadStudies();
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/dashboard/case-studies/${id}/publish`);
      loadStudies();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleAiCheck = async (id) => {
    if (!aiQuota.eligible) { setAiResult({ error: 'AI check is a TurboCharge-only feature.' }); return; }
    if (aiQuota.remaining <= 0) { setAiResult({ error: `Monthly AI pull quota reached (${aiQuota.used}/${aiQuota.limit}). Resets on the 1st.` }); return; }
    setAiChecking(true); setAiResult(null);
    try {
      const { data } = await api.post(`/dashboard/case-studies/${id}/ai-check`);
      setAiResult(data);
      if (data.corrected_content && data.corrected_content !== content) {
        setContent(data.corrected_content);
      }
      aiQuota.refresh();
    } catch (e) { setAiResult({ error: formatApiError(e) }); }
    finally { setAiChecking(false); }
  };

  const handlePhotoUpload = async (studyId, file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/dashboard/case-studies/${studyId}/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadStudies();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDeletePhoto = async (studyId, index) => {
    await api.delete(`/dashboard/case-studies/${studyId}/photos/${index}`).catch(() => {});
    loadStudies();
  };

  const startEdit = (s) => {
    setEditing(s); setTitle(s.title); setContent(s.content); setShowForm(true); setAiResult(null); setError('');
  };

  if (!isPremium) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="casestudies-premium-gate">
        <Crown size={40} className="text-amber-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>In Doctor's Own Words</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">Share your case studies with patients. Publish 1 article per month with up to 3 photos. Premium feature.</p>
        <a href="/upgrade" className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 rounded-full bg-sky-600 text-white text-sm font-medium hover:bg-sky-700">
          <Crown size={14} /> Upgrade to Premium
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="casestudies-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>In Doctor's Own Words</h3>
          <p className="text-xs text-slate-500">Share case studies with patients. 1 per month, up to 10 lines, 3 photos max.</p>
        </div>
        {!showForm && (
          <button onClick={() => { setShowForm(true); setEditing(null); setTitle(''); setContent(''); setError(''); setAiResult(null); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
            data-testid="new-case-study-btn">
            <Plus size={14} /> New Case Study
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14} /> {error}</div>}

      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-sky-200 p-6 space-y-4" data-testid="case-study-form">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={120}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
              placeholder="e.g., Successful Septoplasty for Chronic Breathing Difficulty" data-testid="case-study-title" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-600">Your Words (max 10 lines)</label>
              <span className={`text-xs font-medium ${lineCount > 10 ? 'text-red-500' : lineCount >= 8 ? 'text-amber-500' : 'text-slate-400'}`}>
                {lineCount}/10 lines
              </span>
            </div>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={8}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none resize-none leading-relaxed"
              placeholder="Write about a case you handled — in your own words. Patients value authenticity over polish."
              data-testid="case-study-content" />
          </div>

          {aiResult && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4" data-testid="ai-suggestions">
              <p className="text-xs font-bold text-violet-700 mb-1 flex items-center gap-1"><SpellCheck size={12} /> AI Spell & Structure Check</p>
              {aiResult.structure_note && <p className="text-xs text-violet-600">{aiResult.structure_note}</p>}
              {!aiResult.structure_note && aiResult.ai_checked && <p className="text-xs text-emerald-600">Looks good! No issues found.</p>}
              {aiResult.error && <p className="text-xs text-red-500">{aiResult.error}</p>}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button onClick={handleSave} disabled={saving || lineCount > 10 || !title.trim() || !content.trim()}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-sky-600 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              data-testid="save-case-study-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? 'Update' : 'Save Draft'}
            </button>
            {editing && (
              <button onClick={() => handleAiCheck(editing.id)} disabled={aiChecking || !aiQuota.eligible || aiQuota.remaining <= 0}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-violet-100 text-violet-700 text-sm font-medium hover:bg-violet-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-check-btn"
                title={!aiQuota.eligible ? 'TurboCharge-only feature' : aiQuota.remaining <= 0 ? `Monthly AI quota used (${aiQuota.used}/${aiQuota.limit})` : 'Runs spelling + structure check'}>
                {aiChecking ? <Loader2 size={14} className="animate-spin" /> : <SpellCheck size={14} />} AI Spell Check
                {aiQuota.eligible && <span className="text-[10px] font-semibold text-violet-500 ml-1">{aiQuota.used}/{aiQuota.limit}</span>}
              </button>
            )}
            <button onClick={() => { setShowForm(false); setEditing(null); setError(''); setAiResult(null); }}
              className="px-5 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-10 text-slate-400 animate-pulse">Loading...</div> : (
        <div className="space-y-4">
          {studies.length === 0 && !showForm && (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="no-case-studies">
              <FileText size={36} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No case studies yet. Share your first one!</p>
            </div>
          )}
          {studies.map(s => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`case-study-${s.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="font-medium text-slate-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h4>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                    <span>{new Date(s.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${s.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {s.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    {s.photos?.length > 0 && <span className="flex items-center gap-0.5"><Image size={10} /> {s.photos.length} photo{s.photos.length > 1 ? 's' : ''}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {s.status === 'draft' && (
                    <>
                      <button onClick={() => handlePublish(s.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100" data-testid={`publish-${s.id}`}>
                        <Send size={11} /> Publish
                      </button>
                      <button onClick={() => startEdit(s)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400" data-testid={`edit-${s.id}`}>
                        <Save size={14} />
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" data-testid={`delete-case-${s.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{s.content}</p>

              {/* Photos */}
              {s.photos?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {s.photos.map((p, i) => (
                    <div key={i} className="relative group">
                      <img src={p.url.startsWith('/api/') ? `${BACKEND}${p.url}` : p.url} alt={p.alt}
                        className="w-24 h-24 rounded-xl object-cover border border-slate-200" />
                      {s.status === 'draft' && (
                        <button onClick={() => handleDeletePhoto(s.id, i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <XCircle size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Photo upload for drafts */}
              {s.status === 'draft' && (s.photos?.length || 0) < 3 && (
                <label className="flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-slate-50 text-slate-500 text-xs font-medium hover:bg-slate-100 cursor-pointer w-fit border border-slate-200" data-testid={`upload-photo-${s.id}`}>
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                  Add Photo ({(s.photos?.length || 0)}/3)
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handlePhotoUpload(s.id, e.target.files[0])} />
                </label>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

